import type { ReadableStreamDefaultReader } from 'node:stream/web'
import { decodeChunk } from '../decode'
import { createWavArrayBuffer } from '../merge'
import { unmute } from './unmute'

export class AudioCtx {
  ctx: AudioContext | null
  compressor: DynamicsCompressorNode
  lastCreation: number
  unmute: any
  latencySamples: number[] = []
  destroyed = true

  listeners: { [key: string]: Set<(...args: any[]) => void> } = {}

  on(event: string, listener: (...args: any[]) => void) {
    if (!this.listeners[event]) {
      this.listeners[event] = new Set()
    }
    this.listeners[event].add(listener)
  }

  off(event: string, listener: (...args: any[]) => void) {
    if (!this.listeners[event]) return
    this.listeners[event].delete(listener)
  }

  destroy() {
    this.destroyed = true
    if (this.unmute) {
      this.unmute.dispose()
      this.unmute = null // 참조 제거
    }
    if (this.compressor) {
      this.compressor.disconnect()
      this.compressor = null // 참조 제거
    }
    if (this.ctx) {
      this.ctx.close()
      this.ctx = null
    }
  }

  async refresh(sampleRate?: number) {
    console.log('refresh 🟢')
    this.destroy()
    this.lastCreation = Date.now()
    const ctx = (this.ctx = new (window.AudioContext || (window as any).webkitAudioContext)({ sampleRate }))
    console.log('refresh 🟢🟢', this.ctx.state)

    if (this.ctx.state === 'running') await this.ctx.suspend()
    if (this.ctx.state === 'suspended') await this.ctx.resume()
    unmute(ctx, false, false)
    const compressor = ctx.createDynamicsCompressor()
    compressor.threshold.setValueAtTime(-50, ctx.currentTime)
    compressor.knee.setValueAtTime(40, ctx.currentTime)
    compressor.ratio.setValueAtTime(12, ctx.currentTime)
    compressor.attack.setValueAtTime(0, ctx.currentTime)
    compressor.release.setValueAtTime(0.25, ctx.currentTime)
    compressor.connect(ctx.destination)
    this.compressor = compressor
    this.destroyed = false

    for (const listener of this.listeners['contextRefresh'] || []) {
      listener('contextRefresh', this)
    }

    return this
  }

  decodeAudioData(data: ArrayBuffer): Promise<AudioBuffer> {
    return this.ctx.decodeAudioData(data)
  }

  createBuffer(channels: number, length: number, sampleRate: number): AudioBuffer {
    return this.ctx.createBuffer(channels, length, sampleRate)
  }

  createBufferSource(): AudioBufferSourceNode {
    return this.ctx.createBufferSource()
  }

  createAnalyser(): AnalyserNode {
    return this.ctx.createAnalyser()
  }

  get currentTime() {
    return this.ctx?.currentTime
  }

  suspend() {
    if (this.ctx) return this.ctx.suspend()
  }

  get destination() {
    return this.compressor
  }

  get outputLatency() {
    if (!this.ctx) return 200
    this.latencySamples.push(this.ctx.outputLatency || 0)
    if (this.latencySamples.length > 10) this.latencySamples.shift()
    return Math.max(...this.latencySamples)
  }

  get sampleRate() {
    return this.ctx.sampleRate
  }

  wav(intBuffer: Int16Array, sampleRate = this.sampleRate) {
    return createWavArrayBuffer(intBuffer.buffer, sampleRate)
  }
}

// Sound controller for streaming audio
export default class SoundController {
  ctx = new AudioCtx()
  active = false
  pr: Promise<[boolean, Int16Array]> | null
  speed = 1
  streaming = true
  first = true
  source: AudioBufferSourceNode | null = null
  suspendTimeout: any
  startedAt = 0
  pausedAt = 0

  listeners: { [key: string]: Set<(...args: any[]) => void> } = {}
  onEnd: () => void

  on(event: string, listener: (...args: any[]) => void) {
    if (!this.listeners[event]) {
      this.listeners[event] = new Set()
    }
    this.listeners[event].add(listener)
  }

  off(event: string, listener: (...args: any[]) => void) {
    if (!this.listeners[event]) return
    this.listeners[event].delete(listener)
  }

  constructor() {
    this.refreshHandler = this.refreshHandler.bind(this)
    this.ctx.on('contextRefresh', this.refreshHandler)
  }

  refreshHandler() {
    for (const listener of this.listeners['contextRefresh'] || []) {
      listener('contextRefresh', this.ctx)
    }
  }

  async playStream(
    reader: ReadableStreamDefaultReader<Uint8Array>,
    handler?: any,
    replay = false
  ): Promise<[boolean, ArrayBuffer | undefined]> {
    const chunks: Uint8Array[] = []
    if ((navigator as any).audioSession) {
      ;(navigator as any).audioSession.type = 'playback'
      await new Promise<void>((resolve) => setTimeout(resolve, 20))
      ;(navigator as any).audioSession.type = 'playback'
      await new Promise<void>((resolve) => setTimeout(resolve, 20))
    }

    // eslint-disable-next-line no-constant-condition
    while (true) {
      const { done, value } = await reader.read()
      if (done) break
      chunks.push(value)
      await new Promise<void>((r) => setTimeout(r, 10))
    }

    const merged = new Uint8Array(chunks.reduce((acc, chunk) => acc + chunk.length, 0))
    let offset = 0
    for (const chunk of chunks) {
      merged.set(chunk, offset)
      offset += chunk.length
    }
    chunks.length = 0

    if (this.suspendTimeout) clearTimeout(this.suspendTimeout)
    if (this.ctx?.destroyed) await this.ctx.refresh()
    const ctx = this.ctx
    const [audioBuffer, intBuffer] = await decodeChunk(ctx, merged, this.speed, replay)
    if (!audioBuffer) return [this.active, undefined]
    if (!this.active) return [this.active, !replay && this.ctx.wav(intBuffer)]

    return new Promise((resolve) => {
      let done = false
      const source = (this.source = ctx.createBufferSource())
      source.buffer = audioBuffer
      this.onEnd = async () => {
        if (this.pausedAt) return
        if (done) return
        done = true
        setTimeout(() => {
          source.disconnect()
        }, ctx.outputLatency * 1000 * 2)
        this.suspendTimeout = setTimeout(() => {
          ctx.destroy()
        }, 3000)
        resolve([this.active, !replay && this.ctx.wav(intBuffer)])
        this.onEnd = null
      }
      source.onended = this.onEnd
      source.connect(ctx.destination)
      source.start()
      this.startedAt = this.ctx.currentTime
      if (handler) handler()
    })
  }

  async playFetch(url: string, handler?: any, replay = false): Promise<[boolean, ArrayBuffer | undefined]> {
    try {
      if (!this.active) return Promise.resolve([true, undefined])
      const response = await fetch(url)
      if (!response.ok) return Promise.resolve([true, undefined])
      const reader = response.body?.getReader()
      if (!reader) return Promise.resolve([true, undefined])
      return this.playStream(reader as any, handler, replay)
    } catch (e) {
      console.log(e)
      return Promise.resolve([true, undefined])
    }
  }

  async playBlob(blob: Blob, handler?: any, replay = false): Promise<[boolean, ArrayBuffer | undefined]> {
    if (!this.active) return Promise.resolve([true, undefined])
    const stream = blob.stream()
    const reader = stream.getReader()
    return this.playStream(reader as any, handler, replay)
  }

  async _play(urls: (string | ReadableStreamDefaultReader<Uint8Array> | Blob)[], handler?: any) {
    const buffers: ArrayBuffer[] = []
    let first = true,
      active = this.active
    for (const file of urls) {
      const [finalized, buffer]: [boolean, ArrayBuffer] =
        typeof file === 'string'
          ? await this.playFetch(file, handler, true)
          : file instanceof Blob
          ? await this.playBlob(file, handler, true)
          : await this.playStream(file, handler, false)
      buffers.push(buffer)
      first = false
      if (!finalized) active = false
    }

    return [active, buffers]
  }

  play(
    urls: (string | ReadableStreamDefaultReader<Uint8Array> | Blob)[],
    handler?: any,
    active = true
  ): Promise<[boolean, ArrayBuffer[]]> {
    this.active = active
    return (this.pr = this.pr
      ? (this.pr.then((r) => this._play(urls, handler)) as any)
      : (this._play(urls, handler) as any))
  }

  pause() {
    if (!this.source) return
    if (this.pausedAt) return
    this.pausedAt = this.ctx.currentTime - this.startedAt
    try {
      this.source.stop()
    } catch (e) {
      /* pass */
    }
    this.source.disconnect()
    this.source.onended = null
    const newSource = this.ctx.createBufferSource()
    newSource.buffer = this.source.buffer
    newSource.onended = this.onEnd
    newSource.connect(this.ctx.destination)
    this.source = newSource
  }

  resume() {
    if (!this.source) return
    if (!this.pausedAt) return
    try {
      this.source.start(0, this.pausedAt)
    } catch (e) {
      console.log(e)
    }
    this.startedAt = this.ctx.currentTime - this.pausedAt
    this.pausedAt = 0
  }

  get progress() {
    if (!this.source) return 0
    if (this.pausedAt) return this.pausedAt / this.source.buffer.duration
    return Math.min(Math.max(this.ctx.currentTime - this.startedAt, 0) / this.source.buffer.duration, 1)
  }

  async stop(): Promise<void> {
    this.active = false
    if (this.source) {
      try {
        this.source.stop()
      } catch (e) {
        /* pass */
      }
      this.source.disconnect()
      this.source.onended?.(null)
      this.source = null
    }
    await this.pr
    this.pr = null
  }

  destroy() {
    for (const listener of this.listeners['destroy'] || []) {
      listener('destroy', this)
    }
    for (const event in this.listeners) {
      this.listeners[event].clear()
    }
    this.listeners = {}
    if (this.ctx) {
      this.ctx.off('contextRefresh', this.refreshHandler)
      this.ctx.destroy()
      this.ctx = null
    }
    if (this.suspendTimeout) {
      clearTimeout(this.suspendTimeout)
      this.suspendTimeout = null
    }
    this.onEnd = null
    return this.stop()
  }
}
